import { useCallback, useEffect, useState } from 'react'

import { ActionModal } from '@ui/components/core'
import { TableList } from '@ui/components/elements'
import { TableListItems } from '@ui/components/elements/TableList/TableList'
import { showToast } from '@ui/components/elements/ToastManager'
import { Organization } from '@ui/interfaces'
import axios from '@ui/lib/api/axiosHelper'
import useAppNavigation from '@ui/lib/hooks/useAppNavigation'
import { useAuth } from '@ui/providers/AuthProvider'
import { RouteNamesEnum } from '@ui/router/routes'

export const OrganizationComponent = () => {
  const [organizations, setOrganizations] = useState<Organization[]>([])
  const [selectedOrganization, setSelectedOrganization] = useState<Organization | null>(null)
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const { navigateTo } = useAppNavigation()
  const auth = useAuth()

  const selectOrgToDelete = (id: number) => {
    setSelectedOrganization(organizations.find((org) => org.id === id) || null)
    setDeleteModalOpen(true)
  }

  useEffect(() => {
    if (!deleteModalOpen) {
      setSelectedOrganization(null)
    }
  }, [deleteModalOpen])

  const fetchOrganizations = useCallback(() => {
    const configuration = {
      url: '/v1/organizations',
    }
    axios
      .get(configuration.url)
      .then((response) => {
        setOrganizations(response.data)
        setDeleteModalOpen(false)
      })
      .catch((err: unknown) => {
        console.log('Failed to get organizations list: ', err)
        showToast.error('Failed to retrieve organizations')
      })
  }, [])

  useEffect(() => {
    fetchOrganizations()
  }, [fetchOrganizations])

  const deleteOrganization = (id: number | undefined) => {
    if (!id) return

    setSelectedOrganization(organizations.find((org) => org.id === id) || null)

    const configuration = {
      url: `/v1/organizations/${id}`,
    }
    axios
      .delete(configuration.url)
      .then(() => {
        showToast.success('Organization has been deleted')
        fetchOrganizations()
      })
      .catch((err: unknown) => {
        console.log('Failed to delete org ', id, ': ', err)
        showToast.error('Failed to delete organization')
      })
  }

  const changeOrganization = (id: number) => {
    auth.setOrganization(id, (err) => {
      if (err) {
        console.log('Failed to delete org ', id, ': ', err)
        showToast.error('Failed to change organization')
      } else {
        const org = organizations.find((org) => org.id === id)
        showToast.success(`You are now in organization ${org?.name || id}`)
        navigateTo(RouteNamesEnum.MANAGE_MEMBERS, { orgId: id })
      }
    })
  }

  return (
    <>
      <ActionModal
        open={deleteModalOpen}
        loading={false}
        title={`Delete Org ${selectedOrganization?.name ?? ''}?`}
        message={`This action cannot be undone. All users and data associated with this organization will be deleted.`}
        handleAction={() => deleteOrganization(selectedOrganization?.id)}
        actionIdentifier={'Delete'}
        dismissAction={() => {
          setDeleteModalOpen(false)
        }}
        dismissIdentifier="Cancel"
      />
      <h2 className="mb-4 text-2xl font-bold">Admin Organization Interface</h2>
      <div className="h-[calc(100vh-15rem)] w-full space-y-4">
        <TableList
          items={organizations as unknown as TableListItems}
          menuItems={[
            { label: 'Change to this org', onClick: (id) => changeOrganization(Number(id)) },
            { label: 'Delete', onClick: (id) => selectOrgToDelete(Number(id)) },
          ]}
        />
      </div>
    </>
  )
}
